// Country and related data constants

export const ALLOWED_COUNTRIES = [
  "United States",
  "United Kingdom",
  "India",
  "Canada",
  "Australia",
  "Germany",
  "France",
  "Japan",
  "Singapore",
  "United Arab Emirates",
  "Italy",
  "Spain",
  "Netherlands",
  "Sweden",
  "China",
  "Brazil",
  "Mexico",
] as const;

export type AllowedCountry = (typeof ALLOWED_COUNTRIES)[number];

// Country to phone code mapping
export const COUNTRY_PHONE_CODES: Record<AllowedCountry, string> = {
  "United States": "+1",
  "United Kingdom": "+44",
  "India": "+91",
  "Canada": "+1",
  "Australia": "+61",
  "Germany": "+49",
  "France": "+33",
  "Japan": "+81",
  "Singapore": "+65",
  "United Arab Emirates": "+971",
  "Italy": "+39",
  "Spain": "+34",
  "Netherlands": "+31",
  "Sweden": "+46",
  "China": "+86",
  "Brazil": "+55",
  "Mexico": "+52",
};

// Country to timezone mappings
export const COUNTRY_TIMEZONES: Record<AllowedCountry, string[]> = {
  "United States": [
    "Eastern Standard Time (EST) - UTC-5",
    "Central Standard Time (CST) - UTC-6",
    "Mountain Standard Time (MST) - UTC-7",
    "Mountain Standard Time (MST) - UTC-7 (Arizona - No DST)",
    "Pacific Standard Time (PST) - UTC-8",
    "Alaska Standard Time (AKST) - UTC-9",
    "Hawaii-Aleutian Standard Time (HST) - UTC-10",
  ],
  "United Kingdom": ["Greenwich Mean Time (GMT) - UTC+0"],
  India: ["Indian Standard Time (IST) - UTC+5:30"],
  Canada: [
    "Newfoundland Standard Time (NST) - UTC-3:30",
    "Atlantic Standard Time (AST) - UTC-4",
    "Eastern Standard Time (EST) - UTC-5",
    "Central Standard Time (CST) - UTC-6",
    "Mountain Standard Time (MST) - UTC-7",
    "Pacific Standard Time (PST) - UTC-8",
  ],
  Australia: [
    "Australian Western Standard Time (AWST) - UTC+8",
    "Australian Central Standard Time (ACST) - UTC+9:30 (No DST)",
    "Australian Central Daylight Time (ACDT) - UTC+10:30",
    "Australian Eastern Standard Time (AEST) - UTC+10 (No DST)",
    "Australian Eastern Daylight Time (AEDT) - UTC+11",
  ],
  Germany: ["Central European Time (CET) - UTC+1"],
  France: ["Central European Time (CET) - UTC+1"],
  Japan: ["Japan Standard Time (JST) - UTC+9"],
  Singapore: ["Singapore Standard Time (SGT) - UTC+8"],
  "United Arab Emirates": ["Gulf Standard Time (GST) - UTC+4"],
  Italy: ["Central European Time (CET) - UTC+1"],
  Spain: ["Central European Time (CET) - UTC+1"],
  Netherlands: ["Central European Time (CET) - UTC+1"],
  Sweden: ["Central European Time (CET) - UTC+1"],
  China: ["China Standard Time (CST) - UTC+8"],
  Brazil: ["Brasília Time (BRT) - UTC-3"],
  Mexico: ["Central Standard Time (CST) - UTC-6", "Mountain Standard Time (MST) - UTC-7", "Pacific Standard Time (PST) - UTC-8"],
};

// Country to recommended currency mapping
// Note: Currency values must match enum values returned by /enums/currencies
export const COUNTRY_CURRENCIES: Record<AllowedCountry, string> = {
  "United States": "USD - US Dollar",
  "United Kingdom": "GBP - British Pound",
  India: "INR - Indian Rupee",
  Canada: "CAD - Canadian Dollar",
  Australia: "AUD - Australian Dollar",
  Germany: "EUR - Euro",
  France: "EUR - Euro",
  Japan: "JPY - Japanese Yen",
  Singapore: "SGD - Singapore Dollar",
  "United Arab Emirates": "AED - UAE Dirham",
  Italy: "EUR - Euro",
  Spain: "EUR - Euro",
  Netherlands: "EUR - Euro",
  Sweden: "EUR - Euro",
  China: "CNY - Chinese Yuan",
  Brazil: "BRL - Brazilian Real",
  Mexico: "MXN - Mexican Peso",
};

// Country to ID proof type mappings
export const COUNTRY_ID_PROOFS: Record<AllowedCountry, string[]> = {
  "United States": [
    "Driver's License",
    "State ID Card",
    "Passport",
    "Passport Card",
    "Permanent Resident Card (Green Card)",
    "Social Security Card",
  ],
  "United Kingdom": [
    "Passport",
    "Driving Licence (Full or Provisional)",
    "Biometric Residence Permit (BRP)",
    "CitizenCard",
    "Electoral Identity Card (Northern Ireland)",
  ],
  India: ["Aadhaar Card", "PAN Card", "Voter ID (EPIC)", "Driving Licence", "Passport"],
  Canada: [
    "Driver's License",
    "Provincial Photo ID Card",
    "Passport",
    "Permanent Resident (PR) Card",
    "Secure Certificate of Indian Status",
    "Health Card (Photo)",
  ],
  Australia: ["Driver's Licence", "Proof of Age Card / Photo Card", "Passport", "Keypass ID", "Medicare Card"],
  Germany: ["Personalausweis (National Identity Card)", "Reisepass (Passport)", "Aufenthaltstitel (Residence Permit)"],
  France: [
    "Carte Nationale d'Identité (CNI)",
    "Passeport (Passport)",
    "Permis de conduire (Driver's License)",
    "Titre de Séjour (Residence Permit)",
  ],
  Japan: ["My Number Card", "Driver's License (Unten Menkyo Sho)", "Residence Card (Zairyu Card)", "Passport"],
  Singapore: [
    "NRIC (National Registration Identity Card)",
    "Employment Pass / S Pass / Work Permit",
    "Passport",
    "Digital IC (Singpass)",
  ],
  "United Arab Emirates": ["Emirates ID", "Passport"],
  Italy: ["Carta d'Identità (Identity Card)", "Passaporto (Passport)", "Patente di guida (Driver's License)"],
  Spain: ["DNI (Documento Nacional de Identidad)", "NIE (Número de Identificación de Extranjero)", "Passport", "Driver's License"],
  Netherlands: ["Dutch Identity Card", "Passport", "Driver's License"],
  Sweden: ["Swedish Identity Card", "Passport", "Driver's License"],
  China: ["Resident Identity Card", "Passport", "Hong Kong/Macau Resident Permit"],
  Brazil: ["RG (General Registry)", "CPF (Individual Taxpayer Registry)", "Carteira de Motorista (Driver's License)", "Passaporte (Passport)"],
  Mexico: ["INE (National Electoral Institute) Card", "Passport", "Driver's License"],
};

export const COUNTRY_BANK_FIELDS: Record<
  AllowedCountry,
  {
    key: string;
    label: string;
    required?: boolean;
  }[]
> = {
  "United States": [
    { key: "routingNumber", label: "Routing Number", required: true },
    { key: "swiftCode", label: "SWIFT Code" },
  ],
  // IFSC Code is now a dedicated vendor field (ifscCode/accountType), not part of this
  // generic per-country bank-metadata mechanism.
  India: [],
  "United Kingdom": [
    { key: "sortCode", label: "Sort Code", required: true },
    { key: "iban", label: "IBAN" },
  ],
  Canada: [
    { key: "transitNumber", label: "Transit Number", required: true },
    { key: "institutionNumber", label: "Institution Number", required: true },
    { key: "swiftCode", label: "SWIFT Code" },
  ],
  Australia: [{ key: "bsbCode", label: "BSB Code", required: true }],
  Germany: [{ key: "iban", label: "IBAN", required: true }],
  France: [{ key: "iban", label: "IBAN", required: true }],
  Japan: [
    { key: "bankCode", label: "Bank Code", required: true },
    { key: "branchCode", label: "Branch Code", required: true },
  ],
  Singapore: [{ key: "swiftCode", label: "SWIFT Code", required: true }],
  "United Arab Emirates": [{ key: "iban", label: "IBAN", required: true }],
  Italy: [{ key: "iban", label: "IBAN", required: true }],
  Spain: [{ key: "iban", label: "IBAN", required: true }],
  Netherlands: [{ key: "iban", label: "IBAN", required: true }],
  Sweden: [{ key: "iban", label: "IBAN", required: true }],
  China: [
    { key: "bankCode", label: "Bank Code", required: true },
  ],
  Brazil: [{ key: "cpf", label: "CPF", required: true }],
  Mexico: [{ key: "clabe", label: "CLABE", required: true }],
};